import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { AvailableJobsFilters, formatCategoryName } from "@/lib/availableJobsUtils";
import industryIcon from "@/assets/jobs/icon_industry.svg";
import locationIcon from "@/assets/jobs/icon_location.svg";
import searchBarIcon from "@/assets/jobs/icon-search_bar.svg";

interface AvailableJobFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  locationFilter: string;
  onLocationChange: (value: string) => void;
  filters: AvailableJobsFilters;
  onFiltersChange: (filters: AvailableJobsFilters) => void;
  availableLocations: string[];
  availableCategories: string[];
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

export function AvailableJobFilters({
  searchQuery,
  onSearchChange,
  locationFilter,
  onLocationChange,
  filters,
  onFiltersChange,
  availableLocations,
  availableCategories,
  hasActiveFilters,
  onClearFilters,
}: AvailableJobFiltersProps) {
  const handleCategoryChange = (value: string) => {
    onFiltersChange({
      ...filters,
      category: value === "all" ? [] : [value]
    });
  };

  return (
    <div className="mb-6">
      {/* Mobile Layout - Stack vertically */}
      <div className="block md:hidden space-y-4">
        <div className="relative w-full">
          <Input
            placeholder="Search jobs by title"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pr-10"
          />
          <img 
            src={searchBarIcon} 
            alt="Search" 
            className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4" 
          />
        </div>
        
        <div className="flex flex-col gap-3">
          <Select 
            value={filters.category.length > 0 ? filters.category[0] : "all"} 
            onValueChange={handleCategoryChange}
          >
            <SelectTrigger className="w-full [&>svg]:text-[#FF7A00] [&>svg]:opacity-100">
              <img src={industryIcon} alt="Department" className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Department</SelectItem>
              {availableCategories.map((category) => (
                <SelectItem key={category} value={category}>
                  {formatCategoryName(category)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={locationFilter} onValueChange={onLocationChange}>
            <SelectTrigger className="w-full [&>svg]:text-[#FF7A00] [&>svg]:opacity-100">
              <img src={locationIcon} alt="Location" className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Location</SelectItem>
              {availableLocations.map((loc) => (
                <SelectItem key={loc} value={loc}>
                  {loc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onClearFilters}
              className="text-gray-600 hover:text-gray-900 font-medium w-full"
            >
              <X className="h-4 w-4 mr-1" />
              Clear filters
            </Button>
          )}
        </div>
      </div>

      {/* Desktop Layout - Single row */}
      <div className="hidden md:flex items-center gap-4">
        <div className="relative flex-1">
          <Input
            placeholder="Search jobs by title"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pr-10"
          />
          <img 
            src={searchBarIcon} 
            alt="Search" 
            className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4" 
          />
        </div>
        
        <div className="flex items-center gap-4">
          <Select 
            value={filters.category.length > 0 ? filters.category[0] : "all"} 
            onValueChange={handleCategoryChange}
          >
            <SelectTrigger className="w-40 [&>svg]:text-[#FF7A00] [&>svg]:opacity-100">
              <img src={industryIcon} alt="Department" className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Department</SelectItem>
              {availableCategories.map((category) => (
                <SelectItem key={category} value={category}>
                  {formatCategoryName(category)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={locationFilter} onValueChange={onLocationChange}>
            <SelectTrigger className="w-40 [&>svg]:text-[#FF7A00] [&>svg]:opacity-100">
              <img src={locationIcon} alt="Location" className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Location</SelectItem>
              {availableLocations.map((loc) => (
                <SelectItem key={loc} value={loc}>
                  {loc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button 
              variant="secondary" 
              size="sm"
              onClick={onClearFilters}
              className="text-gray-600 hover:text-gray-900 font-medium [&>svg]:text-[#FF7A00] [&>svg]:opacity-100"
            >
              <X className="h-4 w-4 mr-1" />
              Clear filters
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
